import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Truck, Camera, MapPin, CalendarDays, Lock, CheckCircle2, Trash2, ImageOff, User, Phone, StickyNote, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

const LOGO = "/tilesmate-logo.png";
const BASE = process.env.REACT_APP_BACKEND_URL;
const methodLabel = (m, o) => ({ needafork: "NeedAFork", van: "Van" }[m] || (m === "other" ? (o || "Other") : "—"));
const fmt = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }); } catch { return d; } };

export default function DeliveryPortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [viewer, setViewer] = useState(null);
  const fileRef = useRef(null);
  const touchX = useRef(null);

  const photos = state.data?.photos || [];
  const nav = (dir) => setViewer((v) => (v === null || photos.length === 0 ? v : (v + dir + photos.length) % photos.length));
  useEffect(() => {
    if (viewer === null) return;
    const onKey = (e) => { if (e.key === "Escape") setViewer(null); else if (e.key === "ArrowRight") nav(1); else if (e.key === "ArrowLeft") nav(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [viewer, photos.length]);

  const load = async () => {
    try {
      const r = await fetch(`${BASE}/api/public/delivery/${token}`);
      if (!r.ok) throw new Error("unavailable");
      setState({ loading: false, data: await r.json(), error: null });
    } catch { setState({ loading: false, data: null, error: "This delivery link isn't available. Ask TilesMate to re-send it." }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const r = await fetch(`${BASE}/api/public/delivery/${token}/photos`, { method: "POST", body: fd });
        if (r.status === 423) { toast.error("This delivery is complete — photos are locked."); break; }
        if (!r.ok) { toast.error("Upload failed — try a smaller image."); continue; }
      }
      toast.success("Photos uploaded — thank you!");
      await load();
    } finally { setUploading(false); }
  };
  const del = async (pid) => {
    try { const r = await fetch(`${BASE}/api/public/delivery/${token}/photos/${pid}`, { method: "DELETE" }); if (!r.ok) throw 0; await load(); }
    catch { toast.error("Couldn't remove photo"); }
  };
  const complete = async () => {
    setCompleting(true);
    try {
      const r = await fetch(`${BASE}/api/public/delivery/${token}/complete`, { method: "POST" });
      if (!r.ok) { toast.error("Couldn't mark as delivered — try again."); return; }
      setConfirming(false);
      toast.success("Delivery confirmed — thank you!");
      await load();
    } finally { setCompleting(false); }
  };

  if (state.loading) return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (state.error) return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-6 text-center">
      <div><ImageOff className="mx-auto mb-3 h-10 w-10 text-slate-300" /><p className="font-display text-lg font-bold text-ink">Link unavailable</p><p className="mt-1 text-sm text-slate-500">{state.error}</p></div>
    </div>
  );
  const d = state.data;
  const locked = d.locked;
  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      <div className="border-b border-slate-200 bg-white px-5 pt-6 pb-5 text-center">
        <img src={LOGO} alt="TilesMate" className="mx-auto h-14 w-auto" />
        <div className="mt-3 font-display text-lg font-bold text-ink" data-testid="portal-title">Delivery photos</div>
        <div className="mt-1.5 inline-block rounded-full bg-slate-100 px-3 py-1 font-mono text-xs font-bold text-slate-600" data-testid="portal-order-number">{d.order_number}</div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {d.company && <div className="font-display text-lg font-bold text-ink">{d.company}</div>}
          {d.delivery_address && <div className="mt-0.5 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5 shrink-0" />{d.delivery_address}</div>}
          {(d.delivery_contact_name || d.delivery_contact_mobile) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600" data-testid="portal-contact">
              {d.delivery_contact_name && <span className="inline-flex items-center gap-1.5 font-medium text-ink"><User className="h-3.5 w-3.5 text-slate-400" />{d.delivery_contact_name}</span>}
              {d.delivery_contact_mobile && <a href={`tel:${d.delivery_contact_mobile}`} className="inline-flex items-center gap-1.5 font-semibold text-primary"><Phone className="h-3.5 w-3.5" />{d.delivery_contact_mobile}</a>}
            </div>
          )}
          {d.delivery_note && (
            <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="portal-delivery-note">
              <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /><span>{d.delivery_note}</span>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700"><CalendarDays className="h-3 w-3" />{fmt(d.fulfil_date)}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-bold text-amber-700"><Truck className="h-3 w-3" />{methodLabel(d.delivery_method, d.delivery_method_other)}</span>
          </div>
        </div>

        {locked ? (
          <div className="mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700" data-testid="portal-locked"><CheckCircle2 className="h-4 w-4" />This delivery is complete — photos are locked.</div>
        ) : (
          <div className="mt-4">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="portal-file-input" onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="portal-upload-btn" className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 text-sm font-bold text-white shadow-sm active:scale-[0.99] disabled:opacity-60">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}{uploading ? "Uploading…" : "Add delivery photos"}
            </button>
            <p className="mt-2 text-center text-[11px] text-slate-400">Take photos of the delivered goods at the site. They go straight to TilesMate.</p>
            {d.photo_count > 0 && (
              <button onClick={() => setConfirming(true)} data-testid="portal-mark-delivered-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-emerald-600 bg-emerald-50 py-3 text-sm font-bold text-emerald-700 active:scale-[0.99]">
                <CheckCircle2 className="h-5 w-5" />Mark as delivered
              </button>
            )}
          </div>
        )}

        <div className="mt-5">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Photos ({d.photo_count})</div>
          {d.photos.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-10 text-center text-sm text-slate-400" data-testid="portal-no-photos">No photos yet.</div>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="portal-photo-grid">
              {d.photos.map((p, i) => (
                <div key={p.id} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <button type="button" onClick={() => setViewer(i)} data-testid={`portal-photo-${p.id}`} className="block h-28 w-full">
                    <img src={`${BASE}/api/public/delivery/${token}/photos/${p.id}?size=thumb`} alt={p.filename} className="h-28 w-full cursor-zoom-in object-cover transition-transform group-hover:scale-[1.03]" loading="lazy" />
                  </button>
                  {!locked && <button onClick={(e) => { e.stopPropagation(); del(p.id); }} data-testid={`portal-del-${p.id}`} className="absolute right-1.5 top-1.5 z-10 grid h-7 w-7 place-items-center rounded-lg bg-black/55 text-white"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </div>
          )}
          {locked && <p className="mt-2 flex items-center justify-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" />Photos can't be changed once delivered.</p>}
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 pb-6 pt-20 backdrop-blur-sm sm:items-center" data-testid="portal-confirm-overlay">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><div className="font-display text-lg font-bold text-ink">Mark as delivered?</div></div>
            <p className="text-sm text-slate-500">This locks the delivery and notifies TilesMate. You won't be able to add or remove photos afterwards.</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirming(false)} disabled={completing} data-testid="portal-confirm-cancel" className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 active:scale-[0.99] disabled:opacity-60">Cancel</button>
              <button onClick={complete} disabled={completing} data-testid="portal-confirm-yes" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white active:scale-[0.99] disabled:opacity-60">
                {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{completing ? "Confirming…" : "Yes, delivered"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewer !== null && photos[viewer] && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-black/95" data-testid="portal-lightbox"
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 40) nav(dx < 0 ? 1 : -1); touchX.current = null; }}>
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <span className="text-sm font-semibold" data-testid="portal-lightbox-counter">{viewer + 1} / {photos.length}</span>
            <button onClick={() => setViewer(null)} data-testid="portal-lightbox-close" className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white active:scale-95"><X className="h-5 w-5" /></button>
          </div>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 pb-4">
            <img key={photos[viewer].id} src={`${BASE}/api/public/delivery/${token}/photos/${photos[viewer].id}`} alt={photos[viewer].filename}
              className="max-h-full max-w-full select-none rounded-lg object-contain" data-testid="portal-lightbox-image" />
            {photos.length > 1 && (
              <>
                <button onClick={() => nav(-1)} data-testid="portal-lightbox-prev" className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white backdrop-blur active:scale-95"><ChevronLeft className="h-6 w-6" /></button>
                <button onClick={() => nav(1)} data-testid="portal-lightbox-next" className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white backdrop-blur active:scale-95"><ChevronRight className="h-6 w-6" /></button>
              </>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex items-center justify-center gap-2 overflow-x-auto px-4 pb-5">
              {photos.map((p, i) => (
                <button key={p.id} onClick={() => setViewer(i)} data-testid={`portal-lightbox-thumb-${i}`}
                  className={`h-12 w-12 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${i === viewer ? "border-white" : "border-transparent opacity-60"}`}>
                  <img src={`${BASE}/api/public/delivery/${token}/photos/${p.id}?size=thumb`} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
